import React, { useReducer, useState } from "react";
import { Container, Grid, Typography, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import AddressTable from "src/components/wallets/addresses/AddressTable";
import AddressList from "src/components/wallets/addresses/AddressList";
import Layout from "src/components/core/Layout";
import { trackEvent } from "src/Amplitude";
import Title from "src/components/common/Title";
import api from "src/store/features/api";
import AddressTesting from "src/components/wallets/addresses/AddressTesting";
import {
  setAddress,
  setMemo,
  setModalOpen,
  setSelectedCurrency,
  setStep,
} from "src/store/features/addressTesting.slice";
import { useAppDispatch } from "src/store/store";
import { CurrencyConfigRes } from "src/store/features/api/generated";
import AddressAmountModal from "src/components/wallets/addresses/AddressAmount";
import { AddressTestingStyles } from "src/components/wallets/common/styles/AddressTesting.styles";
import { ManageAddressSkeleton } from "src/components/common/Skeletons";
import { useUserSlice } from "src/store/features/user.slice";
import GuestBanner from "src/components/dashboard/GuestBanner";

export type RowDataType = {
  code: string;
  name: string;
  logoUrl?: string;
  format: string;
  memo?: string;
  address: string;
  nickname?: string;
  status: string;
  depositable?: boolean;
  currencyConfig: CurrencyConfigRes;
};

const NO_ADDRESS_TITLE = "Seems like you don’t have any address with us!" as const;
const NO_ADDRESS_ERROR =
  "Start creating your address by clicking on the “New address” button or contact your broker for assistance." as const;

const Addresses = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [useDefaultTheme, toggle] = useReducer((theme: any) => !theme, true);
  const [selectedRow, setSelectedRow] = useState<RowDataType | null>(null);
  const [amountModalOpen, setAmountModalOpen] = useState(false);
  const { isUnverified } = useUserSlice();

  const classes = AddressTestingStyles();

  const {
    data: cryptoAddresses = [],
    isLoading: isLoadingAddress,
    refetch: refetchAddress,
    isFetching: isFetchingAddress,
  } = api.useGetUserCryptoAddressesQuery({ onlyVerified: false });

  const addresses = cryptoAddresses ? cryptoAddresses : [];
  // IF PAGE REFRESH, GET CRYPTO ADDRESSES
  const noAddresses = cryptoAddresses.length === 0;
  const dispatchRedux = useAppDispatch();

  const handleOpen = (rowData: RowDataType) => {
    // Resume an "Email 2FA required" address at the email-2FA step. The TR
    // feature (D3) renumbered the steps — step 2 is now the Travel-Rule
    // "Wallet Ownership" question and step 4 is the email/2FA verify screen.
    // This handler historically used step 2 (the old 2FA step); post-TR that
    // wrongly surfaced the TR ownership screen for a pending address,
    // regardless of the TR flags. The address is already created and any TR
    // data was collected before the POST, so the only thing left is 2FA.
    trackEvent("Address_Testing_Verify_Ammount_Opened");
    dispatchRedux(setAddress(rowData.address));
    dispatchRedux(setMemo(rowData.memo || ""));
    dispatchRedux(setSelectedCurrency(rowData.currencyConfig));
    dispatchRedux(setStep(4));
    dispatchRedux(setModalOpen(true));
  };

  const handleRowClick = (el: RowDataType) => {
    if (el.status === "Testing Sent") {
      trackEvent("Address_Testing_Verify_Amount_Verify");
      setSelectedRow(el);
      setAmountModalOpen(true);
    } else if (el.status === "Email 2FA required") {
      trackEvent("Address_Testing_Verify_Email_2FA_Row_Clicked");
      handleOpen(el);
    } else if (el.status === "Pending") {
      return;
    }
  };

  React.useEffect(() => {
    trackEvent("Visit_Page", { page: "Addresses" });
  }, []);

  const formatedAddresses = addresses.map((address: any) => {
    return {
      code: address.currencyConfig.code,
      name: address.currencyConfig.name,
      logoUrl: address.currencyConfig.logoUrl,
      format: address.format,
      memo: address.memo,
      address: address.address,
      status: address.status,
      depositable: address.currencyConfig.depositable,
      currencyConfig: address.currencyConfig,
      nickname: address.nickname,
    };
  });
  const isFormattedAddressEmpty = formatedAddresses.length <= 0 && !isLoadingAddress;
  const Header = () => {
    return (
      <Grid container alignItems="center">
        <Grid item xs={6} sx={{ marginBottom: "8px" }}>
          <Title title={{ partB: "Manage Addresses" }} />
        </Grid>
      </Grid>
    );
  };

  return (
    <Layout toggleTheme={toggle} useDefaultTheme={useDefaultTheme}>
      <AddressAmountModal
        open={amountModalOpen}
        handleClose={() => setAmountModalOpen(false)}
        selectedRow={selectedRow}
        refetchAddress={refetchAddress}
      />
      <Container>
        <Header />
        <Grid
          container
          justifyContent="space-between"
          style={{
            marginBottom: "20px",
            paddingLeft: "8px",
            alignItems: "center",
          }}>
          <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
            If you would like to cancel or remove a wallet address, please contact us at
            support@calebandbrown.com
          </Typography>
          <Grid item width="157px">
            <span>
              <AddressTesting refetchAddress={refetchAddress} />
            </span>
          </Grid>
        </Grid>
        {isUnverified && (
          <GuestBanner variant={2} featureName="address" descriptionPrefix="To create an address" />
        )}
        {isFormattedAddressEmpty && !isUnverified && (
          <Grid
            justifyContent="center"
            alignItems={"center"}
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: {
                xs: "column",
                sm: "column",
                md: "row",
              },
            }}>
            <Grid
              item
              xs={6}
              sx={{
                flex: 1,
                flexGrow: 1,
              }}>
              <div>
                <Typography className={classes.noAddrressTitle}>{NO_ADDRESS_TITLE}</Typography>
                <Typography className={classes.noAddrressError}>{NO_ADDRESS_ERROR}</Typography>
              </div>
            </Grid>
            <Grid item xs={6}>
              <img
                src="../static/image/bitcoinImg.png"
                alt="no-addresses-img"
                style={{ width: "300px", height: "200px" }}
              />
            </Grid>
          </Grid>
        )}
        {(isLoadingAddress || isFetchingAddress) && (
          <Box sx={{ height: "300px" }}>
            <ManageAddressSkeleton />
          </Box>
        )}
        {!noAddresses && !isFetchingAddress && (
          <>
            {formatedAddresses.length >= 1 && (
              <>
                {isMobile ? (
                  <AddressList data={formatedAddresses} handleRowClick={handleRowClick} />
                ) : (
                  <Grid
                    container
                    item
                    xs={12}
                    sx={{
                      height: "100%",
                      marginBottom: "20px",
                      overflowY: "scroll",
                      "&::-webkit-scrollbar": {
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        borderRadius: "4px",
                      },
                    }}>
                    <AddressTable data={formatedAddresses} handleRowClick={handleRowClick} />
                  </Grid>
                )}
              </>
            )}
          </>
        )}
      </Container>
    </Layout>
  );
};

export default Addresses;
